import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { RawTransactionDto } from 'api/Transaction/Types/RawTransactionDto'
import { FlexBox } from 'components/Box'
import { Spinner } from 'components/Spinner'
import { createGridState } from 'features/DataGrid'
import uniqBy from 'lodash/uniqBy'
import { GridFilterActionState } from 'store/grid'
import { pageableSettings } from 'store/grid/grid.slice'
import { PageQuery } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, BuildQueryProps } from 'utils/QueryHelpers'

import transactionOverviewWalletOrClientColumn from './Details/TransactionsOverviewForWalletOrClient/transactionOverviewWalletOrClientColumn'

interface QueryProps {
  search?: BuildQueryProps
}

interface Props {
  walletId: string
  query: QueryProps
  selectedTransactions: RawTransactionDto[]
  getTransactions(transactions: RawTransactionDto[]): void
}

export const SelectMultipleTransactions = (props: Props): JSX.Element => {
  const [isLoading, setLoading] = useState<boolean>(true)
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const [selectedTransactions, setSelectedTransactions] = useState<RawTransactionDto[]>([])
  const walletsApiClient = useApiClient(WalletsApiClient)
  const [transactions, setTransactions] = useState<RawTransactionDto[]>([])

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      const search = filter ?? props.query.search
      if (search) {
        return buildQuery(search) ?? null
      }
    },
    [props.query.search] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const fetchTransactionsFactory = useCallback(
    async (query?: PageQuery) => {
      return await walletsApiClient.getWalletTransactions(props.walletId, {
        ...query,
        search: query?.search || getQuery(),
      })
    },
    [getQuery, props.walletId] // eslint-disable-line react-hooks/exhaustive-deps
  )

  useEffect(() => {
    ;(async () => {
      await fetchGrid(async (): Promise<PageData<RawTransactionDto[]> | undefined> => {
        const paginationData = await fetchTransactionsFactory()
        if (paginationData) {
          setTransactions(paginationData.items)
        }
        return paginationData
      })
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (callback: () => Promise<PageData<RawTransactionDto[]> | undefined>) => {
    setLoading(true)

    const gridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback()
    if (response?.totalResults) {
      const total = response.totalResults

      setGridFilterState({
        ...gridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    setLoading(false)
  }

  const handlePageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip,
      recordsPerPage: take,
    })

    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
    })

    const paginationData = await fetchTransactionsFactory({
      pageIndex,
      pageSize,
    })
    if (paginationData) {
      const total = paginationData.totalResults

      setGridFilterState({
        ...gridFilterState,
        take,
        skip,
        pageIndex,
        total,
      })
      if (paginationData?.items) {
        setTransactions(selectTransactions(paginationData.items))
      }
    }
  }

  const selectTransactions = (transactionItems: RawTransactionDto[]) =>
    transactionItems.map((transaction: RawTransactionDto) => {
      const match = selectedTransactions.find((x) => x.id === transaction.id)
      const isSelected = !!match
      return { ...transaction, selected: isSelected }
    })

  const handleSelect = () => {
    props.getTransactions(selectedTransactions)
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    if (!event.dataItem.selected) {
      const transaction = transactions.find((x) => x.id === event.dataItem.id)
      if (transaction) {
        setSelectedTransactions([...selectedTransactions, transaction])
      }
    } else {
      setSelectedTransactions(selectedTransactions.filter((x) => x.id !== event.dataItem.id))
    }

    setTransactions(
      transactions.map((item) => {
        if (item.id === event.dataItem.id) {
          return { ...item, selected: !event.dataItem.selected }
        }
        return item
      })
    )
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setTransactions(transactions.map((t) => ({ ...t, selected: isChecked })))

    const transactionsSelected = isChecked
      ? uniqBy([...transactions, ...selectedTransactions], 'id')
      : selectedTransactions.filter((selectedTrans) => {
          const hasTransaction = transactions.find((x) => x.id === selectedTrans.id)

          return !hasTransaction
        })

    setSelectedTransactions(transactionsSelected)
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <div style={{ margin: 16 }}>
      <Grid
        {...gridFilterState}
        resizable
        data={transactions}
        selectedField='selected'
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={handlePageChange}
        pageable={pageableSettings}
        style={{ maxHeight: '70vh' }}
      >
        <Column
          field='selected'
          width='50px'
          headerSelectionValue={transactions.findIndex((dataItem) => !dataItem.selected) === -1}
        />
        {transactionOverviewWalletOrClientColumn
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
      </Grid>
      <FlexBox style={{ margin: 32 }} alignItems='center' justifyContent='center'>
        <Button onClick={handleSelect}>Save</Button>
      </FlexBox>
    </div>
  )
}
